import React from 'react';

import { Aedu } from 'global';

import { getAutoProps } from 'js/auto_props/client';
import { ToastProvider } from 'js/design_system/toasts/ToastProvider';
import { ReaderMessageContext } from 'js/liswp/work_actions/ReaderMessageContext';

import {
  BulkDownloadableWork,
  bulkDownloadDownloadSource,
  bulkDownloadImpressionSource,
  BulkDownloadTrigger,
  giveGatedDownloadOnUpsellClose,
  externalFallbackUrl,
  navigateToCheckout,
} from './bulk_download_helper';
import renderModal from 'js/utils/renderModal';
import { markModalOpening } from 'js/utils/modalPresence';
import {
  maybeStartAutofake,
  quietAutofakeFlags,
} from 'js/autofake/maybeStartAutofake';
import type { BundleWork } from 'js/bulk_download/work_cards_shared/types';
import { fetchFeatureEnabled } from 'js/research_radar/api/researchRadarApi';
import { loadWonderchatScript } from './wonderchat_widget';
import { ModalContext } from 'js/bulk_download/autotest/AutotestBulkDownload';
import { publishedArm } from 'js/bulk_download/autotest/publishedArm';
import type { Props as PremiumModalProps } from './WorkCardsBulkDownloadUpsellModal/PremiumWorkCardsBulkDownloadModal';

type PremiumModalPassthrough = Pick<
  PremiumModalProps,
  'landingPageUrl' | 'exactWorkIds' | 'exactWorkBuckets'
>;

interface Options extends PremiumModalPassthrough {
  trigger: BulkDownloadTrigger;
  work: BulkDownloadableWork;
  // `gatedDownload` indicates whether the single-work download should be gated
  // behind the upsell (i.e., user gets the download when the modal closes).
  gatedDownload?: boolean;
  autoStartBulkDownload?: boolean;
  purchased?: boolean;
  readerMessageLimitReached?: boolean;
  setReaderMessageLimitReached?: (value: boolean) => void;
  /** Whether the user passes the synchronous CiteRight encore-email
   * eligibility filter on this work (post_download_data API). Sourced
   * server-side from Treatments::CiterightDownloadInvitation
   * .eligible_at_download_time?. Used to gate AB test bucketing for the
   * CITE-232 marketing modal. */
  citerightMarketingModalEligible?: boolean;
}

export function bulkDownloadTrialPrice(): string | undefined {
  try {
    return getAutoProps('BulkDownloadFlags')?.trialPrice || undefined;
  } catch {
    return undefined;
  }
}

export async function showBulkDownloadModal({
  trigger,
  work,
  gatedDownload,
  autoStartBulkDownload,
  purchased,
  readerMessageLimitReached = true,
  setReaderMessageLimitReached = () => {},
  citerightMarketingModalEligible = false,
  landingPageUrl,
  exactWorkIds,
  exactWorkBuckets,
}: Options): Promise<void> {
  const isPremium: boolean = Aedu.User?.current?.premium;
  const shouldStartWorkDownloadOnUpsellClose = gatedDownload === true;

  // Mark a modal as opening SYNCHRONOUSLY, before the async import below, so a
  // deferred launcher (the full screen premium modal, shown after
  // lcpFinalized()) that runs in a microtask after this same click sees that a
  // modal is on its way and skips, instead of stacking on top. Released when the
  // modal closes, or on import failure so the flag can't get stuck.
  const releaseModalPresence = markModalOpening();

  // The premium/purchased modal path must mirror the server's authorization
  // (Api::V0::AttachmentsController#bulk_download_files: premium, purchased,
  // or post-upgrade access) — a user routed here whom the server won't
  // authorize gets a dead "Download PDF Pack" button that 401s. The
  // pandemic-era coronavirus-work carve-out lived here until 2026 doing
  // exactly that: its server-side counterpart was long gone, so every free
  // user on a covid/virus-titled work got the broken premium modal instead
  // of the upsell-to-checkout path.
  if (isPremium || purchased) {
    const radarEnabledPromise = isPremium
      ? fetchFeatureEnabled()
      : Promise.resolve(false);

    Promise.all([
      import(
        './WorkCardsBulkDownloadUpsellModal/PremiumWorkCardsBulkDownloadModal'
      ),
      radarEnabledPromise,
    ])
      .then(([module, radarEnabled]) => {
        const PremiumModal = module.default;
        renderModal(
          (onClose) => (
            <PremiumModal
              onClose={onClose}
              sourceWork={{
                id: work.id,
                title: work.title,
                slug: work.slug,
                doi: work.doi,
                hasDownloadableAttachment: !!work.attachment.id,
              }}
              downloadImmediately={autoStartBulkDownload}
              researchRadarEnabled={radarEnabled}
              landingPageUrl={landingPageUrl}
              exactWorkIds={exactWorkIds}
              exactWorkBuckets={exactWorkBuckets}
            />
          ),
          { afterClose: releaseModalPresence },
        );
        if (isPremium && trigger.startsWith('swp-')) {
          loadWonderchatScript();
        }
      })
      .catch(() => releaseModalPresence());
  } else {
    const resumeDownloadChain = () => {
      if (shouldStartWorkDownloadOnUpsellClose) {
        maybeDownloadWork(
          trigger,
          work,
          readerMessageLimitReached,
          setReaderMessageLimitReached,
          citerightMarketingModalEligible,
        );
      }
    };

    // Autofake (a fake-door product test) INTERPOSES here rather than appending:
    // when eligible AND the decline wins its roll, it shows in place of the gated
    // download, then resumes it on every exit. Otherwise it returns false
    // synchronously and the chain runs unchanged, inside this click's user
    // activation. Everything Autofake lives in js/autofake — this file only asks.
    const afterFirstModalClose = () => {
      const interposed = maybeStartAutofake({
        trigger,
        work,
        gatesADownload: shouldStartWorkDownloadOnUpsellClose,
        packWorks: autofakeWorks,
        onContinue: resumeDownloadChain,
      });
      if (!interposed) resumeDownloadChain();
    };

    const ctx: ModalContext = {
      trigger,
      published: publishedArm(),
      trialPrice: bulkDownloadTrialPrice(),
    };

    // When the user clicks the CTA, the modal snapshots the current filter
    // state and work IDs into sessionStorage (see
    // bulk_download_checkout_state.ts). After checkout, the premium modal
    // consumes that snapshot and uses the saved IDs for the auto-download so
    // the user gets exactly what they saw before upgrading. Storage is keyed
    // by the source work ID, has a 30-min TTL, and is cleared on dismiss.
    const landingPageHostname = landingPageUrl
      ? (() => {
          try {
            return new URL(landingPageUrl).hostname;
          } catch {
            return undefined;
          }
        })()
      : undefined;
    // For works where licenseAllowsAttachment is false, we cannot serve the
    // PDF ourselves — the paper must be fetched from the publisher's site.
    // In that case, the upsell secondary action should direct the user to
    // the paper externally (via landing page, DOI resolver, or Scholar)
    // rather than showing a plain "No thanks" that leaves them with nothing.
    //
    // This condition is intentionally strict: user-owned works and system-owned
    // works whose license permits hosting the attachment both get the default
    // "No thanks, I'll stay on the free tier" dismiss, even if they have a DOI.
    const attachmentExternalOnly = work.licenseAllowsAttachment === false;
    const fallbackUrl =
      !landingPageHostname && attachmentExternalOnly
        ? externalFallbackUrl(work.doi, work.title)
        : undefined;
    const externalUrl = landingPageHostname ? landingPageUrl : fallbackUrl;
    const secondaryActionLabel = landingPageHostname
      ? `No thanks, continue to ${landingPageHostname}`
      : work.doi && attachmentExternalOnly
        ? 'No thanks, view via DOI'
        : fallbackUrl
          ? 'No thanks, search Google Scholar'
          : undefined;

    // The papers the upsell modal loaded, kept so Autofake can show the same set
    // without a second request — useFilteredWorks holds them in component state,
    // so they are gone once that modal unmounts. Captured only when Autofake is
    // on: with the flag off `onWorksLoaded` is undefined and the modal's effect
    // is a no-op, leaving its behaviour untouched. Only the FIRST non-empty load
    // is kept — later loads are filter results, and Autofake's contract is the
    // pack the user was originally shown, not the last filtered subset.
    const autofakeEnabled = !!quietAutofakeFlags()?.enabled;
    let autofakeWorks: BundleWork[] = [];

    const sharedProps = {
      onPrimaryCtaClicked: () =>
        navigateToCheckout(trigger, work.id, new Set()),
      impressionSource: bulkDownloadImpressionSource('upsell', trigger),
      sourceWork: work,
      secondaryActionLabel,
      onSecondaryAction: externalUrl
        ? () => window.open(externalUrl, '_blank', 'noopener,noreferrer')
        : undefined,
      hideIncludedWorkValueProp: !!landingPageUrl,
      onWorksLoaded: autofakeEnabled
        ? (works: BundleWork[]) => {
            if (autofakeWorks.length === 0) autofakeWorks = works;
          }
        : undefined,
    };

    import('./WorkCardsBulkDownloadUpsellModal')
      .then((upsellModule) => {
        const UpsellModal = upsellModule.default;
        renderModal(
          (onClose) => (
            <UpsellModal onClose={onClose} ctx={ctx} {...sharedProps} />
          ),
          {
            afterClose: () => {
              releaseModalPresence();
              afterFirstModalClose();
            },
          },
        );
      })
      .catch(() => releaseModalPresence());
  }
}

function maybeDownloadWork(
  trigger: BulkDownloadTrigger,
  work: BulkDownloadableWork,
  readerMessageLimitReached: boolean,
  setReaderMessageLimitReached: (value: boolean) => void,
  citerightMarketingModalEligible: boolean,
): void {
  if (!giveGatedDownloadOnUpsellClose(trigger)) return;
  // Skip if there is no stored attachment (e.g. system-owned works with an
  // external landing page URL).  Clicking an empty href would navigate to the
  // current page rather than downloading a file.
  if (!work.attachment.url) return;
  const link = document.createElement('a');
  link.href = work.attachment.url;
  link.setAttribute('target', '_blank');
  link.setAttribute('style', 'display: none');
  document.body.appendChild(link);
  link.click();

  if (citerightMarketingModalEligible) {
    // Mark presence synchronously so the queued FSP modal waits for this
    // post-download modal too (released when it closes / on import failure).
    const releaseCiteright = markModalOpening();
    import('js/liswp/modals_ds/CiterightMarketingModal')
      .then((module) => {
        const { CiterightMarketingModal } = module;
        renderModal(
          (onClose) => (
            <CiterightMarketingModal onClose={onClose} workId={work.id} />
          ),
          { afterClose: releaseCiteright },
        );
      })
      .catch(() => releaseCiteright());
    return;
  }

  // Skip reader message modal if no authors (system-owned works)
  const primaryAuthor = work.authors[0];
  if (readerMessageLimitReached || !primaryAuthor) return;

  // Mark presence synchronously so the queued FSP modal waits for the reader
  // message modal too (released when it closes / on import failure).
  const releaseReaderMessage = markModalOpening();
  import('js/liswp/modals_ds/ReaderMessageModal')
    .then((module) => {
      const { ReaderMessageModal } = module;
      renderModal(
        (onClose) => (
          <ToastProvider>
            <ReaderMessageContext.Provider
              value={{
                readerMessageLimitReached,
                setReaderMessageLimitReached,
                citerightMarketingModalEligible,
                setCiterightMarketingModalEligible: () => {},
              }}
            >
              <ReaderMessageModal
                onClose={onClose}
                primaryAuthor={{
                  id: primaryAuthor.id,
                  displayName: primaryAuthor.displayName,
                  photo: primaryAuthor.photo,
                }}
                workId={work.id}
                attachmentId={work.attachment.id}
                readerMessageSource={bulkDownloadDownloadSource(trigger)}
              />
            </ReaderMessageContext.Provider>
          </ToastProvider>
        ),
        { afterClose: releaseReaderMessage },
      );
    })
    .catch(() => releaseReaderMessage());
}
